<!-- src/apps/secret/components/canonical/SecretDisplayCase.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import SecretDisplayHelpContent from '@/apps/secret/components/SecretDisplayHelpContent.vue';
  import { useClipboard } from '@/shared/composables/useClipboard';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { Secret, SecretDetails } from '@/schemas/shapes/v3/secret';
  import { computed } from 'vue';

  import BaseSecretDisplay from './BaseSecretDisplay.vue';

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();
  const signinEnabled = computed(
    () => bootstrapStore.authentication?.enabled
      && bootstrapStore.authentication?.signin
  );
  const helpEnabled = computed(() => bootstrapStore.ui?.help?.enabled ?? true);

  interface Props {
    record: Secret | null;
    details: SecretDetails | null;
    submissionStatus?: {
      status: 'idle' | 'submitting' | 'success' | 'error';
      message?: string;
    };
  }

  const props = defineProps<Props>();

  // Generate unique IDs for ARIA attributes based on record identifier
  const secretContentId = computed(() => `secret-content-${props.record?.identifier || 'unknown'}`);
  const statusMessageId = computed(() => `status-message-${props.record?.identifier || 'unknown'}`);

  const alertClasses = computed(() => ({
    'mb-4 p-4 border': true,
    'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-200':
      props.submissionStatus?.status === 'error',
    'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-500/30 dark:bg-brand-900/20 dark:text-brand-100':
      props.submissionStatus?.status === 'success',
  }));

  const { isCopied, copyToClipboard } = useClipboard();

  // Use different translation keys for copy button text based on state
  const copyButtonText = computed(() =>
    isCopied.value ? t('web.STATUS.copied') : t('web.LABELS.copy_to_clipboard')
  );

  const copySecretContent = async () => {
    if (props.record?.secret_value === undefined) {
      return;
    }

    await copyToClipboard(props.record?.secret_value);

    // Announce copy success to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.textContent = t('web.secrets.secret_content_copied_to_clipboard');
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  };
</script>

<template>
  <!-- Terminal panel (Figma Make reveal chrome) -->
  <!-- prettier-ignore-attribute class -->
  <BaseSecretDisplay
    class="secret-display-case relative border border-day-line bg-day
      dark:border-white/[0.07] dark:bg-night-100">
    <!-- Header section with status strip, title and help link -->
    <template #header>
      <!-- Status strip -->
      <!-- prettier-ignore-attribute class -->
      <div
        data-testid="secret-status-strip"
        class="flex h-9 items-center justify-between gap-4 overflow-x-auto
          border-b border-day-line bg-day-200 px-3.5
          dark:border-white/[0.13] dark:bg-night-200">
        <div class="flex shrink-0 items-center gap-4">
          <!-- prettier-ignore-attribute class -->
          <span
            class="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.08em]
              text-emerald-600 dark:text-emerald-400">
            <span
              class="inline-block size-[5px] shrink-0 rounded-full bg-[#00C47A] shadow-[0_0_6px_#00C47A]"
              aria-hidden="true"></span>
            {{ t('reveal_terminal.status_revealed') }}
          </span>
          <!-- prettier-ignore-attribute class -->
          <span
            class="font-mono text-[9px] uppercase tracking-[0.08em]
              text-day-ink-faint dark:text-night-ink-faint">
            AES-256
          </span>
        </div>
        <!-- prettier-ignore-attribute class -->
        <span
          class="shrink-0 font-mono text-[9px] uppercase tracking-[0.08em]
            text-day-ink-faint dark:text-night-ink-faint">
          {{ t('reveal_terminal.no_reads_remaining') }}
        </span>
      </div>

      <div class="p-4 pb-0 sm:p-6 sm:pb-0">
        <!-- Hero label -->
        <div class="mb-4 flex items-center gap-2">
          <span
            class="inline-block size-1.5 bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]"
            aria-hidden="true"></span>
          <!-- prettier-ignore-attribute class -->
          <span
            class="font-mono text-[10px] uppercase tracking-[0.08em]
              text-day-ink-faint dark:text-night-ink-faint">
            {{ t('reveal_terminal.revealed_label') }}
          </span>
        </div>

        <div class="mb-4 flex items-start justify-between">
          <h1
            class="font-mono text-xl font-bold tracking-tight text-day-ink dark:text-night-ink"
            id="secret-display-heading">
            {{ t('web.shared.post_reveal_default') }}
          </h1>
          <!-- Help Modal Trigger positioned to the right -->
          <NeedHelpModal
            v-if="helpEnabled"
            link-icon-name="question-mark-circle-16-solid"
            link-text-label="">
            <!-- prettier-ignore-attribute class -->
            <button
              type="button"
              class="ml-4 text-sm font-medium
                text-brand-600 hover:text-brand-700
                focus:underline focus:outline-none dark:text-brand-500 dark:hover:text-brand-400">
              {{ t('web.COMMON.need_help') }}?
            </button>
            <template #content>
              <SecretDisplayHelpContent />
            </template>
          </NeedHelpModal>
        </div>

        <!-- Alert display -->
        <div
          v-if="submissionStatus?.status === 'error' || submissionStatus?.status === 'success'"
          :class="alertClasses"
          role="alert"
          :id="statusMessageId"
          aria-live="polite">
          <div class="flex">
            <div class="shrink-0">
              <svg
                v-if="submissionStatus.status === 'error'"
                class="size-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd" />
              </svg>
              <svg
                v-else
                class="size-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm">
                {{
                  submissionStatus.message ||
                    (submissionStatus.status === 'error' ? 'An error occurred' : 'Success')
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <div class="relative px-4 sm:px-6">
        <label
          :for="secretContentId"
          class="sr-only">
          {{ t('web.secrets.secret_content') }}
        </label>
        <!-- prettier-ignore-attribute class -->
        <textarea
          v-if="record?.secret_value"
          :id="secretContentId"
          class="w-full resize-none border border-brand-500/25 bg-brand-500/[0.08] px-4 py-3
            font-mono text-base leading-tight tracking-wide text-day-ink transition-colors
            focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500
            dark:border-brand-400/25 dark:bg-brand-500/[0.12] dark:text-night-ink dark:focus:border-brand-400"
          readonly
          :rows="details?.display_lines ?? 4"
          :value="record?.secret_value"
          aria-describedby="copy-instructions"
          :aria-label="t('web.secrets.secret_content')"
          data-testid="secret-content"></textarea>
        <div
          v-else
          class="text-red-600 dark:text-red-400"
          role="alert">
          {{ t('web.secrets.secret_value_not_available') }}
        </div>
        <p
          id="copy-instructions"
          class="sr-only">
          {{ t('web.COMMON.press_copy_button_below') }}
        </p>

        <!-- Honest copy: the backend destroyed the content on this read -->
        <p class="mt-3 font-mono text-[10px] leading-relaxed text-day-ink-faint dark:text-night-ink-faint">
          {{ t('reveal_terminal.destroyed_note') }}
        </p>
      </div>
    </template>

    <template #warnings>
      <div>
        <!-- prettier-ignore-attribute class -->
      </div>
    </template>

    <template #cta>
      <div class="p-4 pt-4 sm:p-6">
        <!-- prettier-ignore-attribute class -->
        <button
          @click="copySecretContent"
          :title="copyButtonText"
          class="inline-flex items-center justify-center bg-brand-600 px-5 py-2.5
            font-mono text-[11px] uppercase tracking-[0.08em] text-white
            transition-colors duration-150 ease-in-out hover:bg-brand-700
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
            dark:focus:ring-offset-night-100"
          :aria-label="copyButtonText"
          :aria-pressed="isCopied">
          <svg
            v-if="!isCopied"
            xmlns="http://www.w3.org/2000/svg"
            class="mr-2 size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            role="img"
            aria-label="Copy icon">
            <title>{{ t('web.LABELS.copy_icon') }}</title>
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            class="mr-2 size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            role="img"
            aria-label="Checkmark icon">
            <title>{{ t('web.LABELS.copied_icon') }}</title>
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7" />
          </svg>
          <span>{{ copyButtonText }}</span>
        </button>

        <!-- Navigation -->
        <div
          v-if="!record?.verification"
          class="mt-10 text-center font-mono text-[11px] text-day-ink-faint dark:text-night-ink-faint">
          <p>
            {{ t('web.secrets.you_can_safely_close_this_tab') }}
          </p>
        </div>
        <div
          v-else-if="signinEnabled"
          class="mt-10">
          <!-- prettier-ignore-attribute class -->
          <a
            href="/signin"
            class="block w-full border border-day-line-2 bg-day px-4 py-2.5 text-center
              font-mono text-[11px] uppercase tracking-[0.08em] text-day-ink-muted
              transition-colors hover:border-brand-500/40 hover:text-day-ink
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
              dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink-muted
              dark:hover:border-brand-500/40 dark:hover:text-night-ink
              dark:focus:ring-offset-night-100"
            :aria-label="t('web.COMMON.login_to_your_account')">
            {{ t('web.COMMON.login_to_your_account') }}
          </a>
        </div>
      </div>
    </template>

    <template #footer>
      <CornerBrackets />
    </template>
  </BaseSecretDisplay>
</template>

<style scoped>
  /* Ensure focus outline is visible in all color schemes */
  :focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
</style>
